import React, { useEffect, useState, useCallback } from "react";
import {
  AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid,
} from "recharts";
import {
  Scale, TrendingUp, TrendingDown, AlertTriangle, Percent, Settings2,
  Download, Pencil, Building2, Store, Trophy, Layers,
} from "lucide-react";
import api from "../lib/api";
import { Card, PageHeader, PageLoader, Badge, Button, Modal } from "../lib/ui";
import { useToast } from "../lib/toast";
import { fmtRp, fmtNum } from "../lib/constants";
import { useT } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";

const STATUS = {
  HEALTHY: { tone: "success", label: "Healthy" },
  MEDIUM: { tone: "warn", label: "Medium" },
  LOW: { tone: "danger", label: "Low Margin" },
  LOSS: { tone: "danger", label: "Loss" },
  UNSET: { tone: "mute", label: "Not Priced" },
};

const shortDate = (d) => (d || "").slice(5);
const pct = (v) => (v == null ? "—" : `${v}%`);

export default function MarginDashboard() {
  const { t } = useT();
  const { hasPermission } = useAuth();
  const canManage = hasPermission("manage_branch_pricing");
  const [branches, setBranches] = useState([]);
  const [branchId, setBranchId] = useState(null);
  const [data, setData] = useState(null);
  const [editRow, setEditRow] = useState(null);
  const [settingsOpen, setSettingsOpen] = useState(false);

  const load = useCallback(() => {
    const url = branchId ? `/pricing/margin-dashboard?branch_id=${branchId}` : "/pricing/margin-dashboard";
    api.get(url).then(({ data }) => setData(data)).catch(() => setData({ rows: [], summary: {}, margin_trend: [] }));
  }, [branchId]);

  useEffect(() => { api.get("/branches").then(({ data }) => setBranches(data)); }, []);
  useEffect(() => { setData(null); load(); }, [load]);

  const exportCsv = async () => {
    const url = branchId ? `/pricing/margin-dashboard/export.csv?branch_id=${branchId}` : "/pricing/margin-dashboard/export.csv";
    const res = await api.get(url, { responseType: "blob" });
    const blob = new Blob([res.data], { type: "text/csv" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = "margin-dashboard.csv";
    a.click();
  };

  if (!data) return <PageLoader />;
  const s = data.summary || {};
  const rows = data.rows || [];
  const trend = data.margin_trend || [];

  return (
    <div className="animate-fade-up" data-testid="margin-dashboard">
      <PageHeader title={t("Margin Dashboard")} subtitle={t("Branch Transfer Pricing & Margin")} icon={Scale}>
        <div className="flex gap-2">
          <Button variant="outline" onClick={exportCsv} data-testid="margin-export-csv"><Download size={16} /> {t("Export CSV")}</Button>
          {canManage && <Button variant="outline" onClick={() => setSettingsOpen(true)} data-testid="margin-settings-btn"><Settings2 size={16} /> {t("Pricing Settings")}</Button>}
        </div>
      </PageHeader>

      <p className="mb-4 rounded-xl bg-blue-50 px-3 py-2 text-xs text-blue-700" data-testid="margin-note">
        {t("Margins compare HPP → Transfer Price → Branch Selling Price. HPP, inventory valuation and all workflows are unchanged.")}
      </p>

      {/* Branch selector */}
      <div className="mb-4 flex flex-wrap gap-2" data-testid="margin-branch-selector">
        <BranchChip label={t("Global (all branches)")} icon={Building2} active={!branchId} onClick={() => setBranchId(null)} testid="margin-branch-global" />
        {branches.map((b) => (
          <BranchChip key={b.id} label={b.name} icon={Store} logo={b.logo} active={branchId === b.id} onClick={() => setBranchId(b.id)} testid={`margin-branch-${b.name}`} />
        ))}
      </div>

      {/* KPI summary */}
      <div className="mb-5 grid grid-cols-2 gap-3 sm:gap-4 lg:grid-cols-4">
        <Kpi icon={Percent} label={t("Avg Central Margin")} value={pct(s.avg_central_margin_pct)} tone="primary" testid="margin-kpi-central" />
        <Kpi icon={Percent} label={t("Avg Branch Margin")} value={pct(s.avg_branch_margin_pct)} tone="info" testid="margin-kpi-branch" />
        <Kpi icon={AlertTriangle} label={t("Items Below Threshold")} value={fmtNum(s.below_threshold ?? 0)} tone="warn" testid="margin-kpi-below" />
        <Kpi icon={TrendingDown} label={t("Selling Below HPP")} value={fmtNum(s.selling_below_hpp ?? 0)} tone="danger" testid="margin-kpi-loss" />
      </div>

      <div className="mb-5 grid grid-cols-1 gap-4 lg:grid-cols-2">
        {/* Margin trend */}
        <Card className="p-4 sm:p-5" data-testid="margin-trend-card">
          <div className="mb-3 flex items-center gap-2"><TrendingUp size={16} className="text-primary" />
            <div><h3 className="font-heading text-sm font-extrabold text-ink">{t("Margin Trend")}</h3>
              <p className="text-[11px] text-ink-mute">{t("Average central margin % · last 30 days")}</p></div></div>
          {trend.some((p) => p.margin_pct != null) ? (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={trend} margin={{ top: 5, right: 8, left: 4, bottom: 0 }}>
                <defs><linearGradient id="mdGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#ea580c" stopOpacity={0.35} /><stop offset="100%" stopColor="#ea580c" stopOpacity={0.02} />
                </linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1ece6" vertical={false} />
                <XAxis dataKey="date" tickFormatter={shortDate} tick={{ fontSize: 10, fill: "#9b8e82" }} interval={5} />
                <YAxis tick={{ fontSize: 10, fill: "#9b8e82" }} width={36} unit="%" />
                <Tooltip formatter={(v) => `${v}%`} />
                <Area type="monotone" dataKey="margin_pct" stroke="#ea580c" strokeWidth={2.5} fill="url(#mdGrad)" connectNulls />
              </AreaChart>
            </ResponsiveContainer>
          ) : <Empty label={t("No confirmed production yet")} />}
        </Card>

        {/* Top contributors */}
        <Card className="p-4 sm:p-5" data-testid="margin-contributors-card">
          <div className="mb-3 flex items-center gap-2"><Layers size={16} className="text-primary" />
            <div><h3 className="font-heading text-sm font-extrabold text-ink">{t("Top Margin Contributors")}</h3>
              <p className="text-[11px] text-ink-mute">{t("Margin contribution = margin × 30-day volume")}</p></div></div>
          {(data.top_contributors || []).length ? (
            <div className="space-y-2.5 py-1">
              {data.top_contributors.map((r, i) => {
                const max = data.top_contributors[0].margin_contribution || 1;
                return (
                  <div key={i} data-testid={`margin-contrib-${i}`}>
                    <div className="mb-1 flex items-center justify-between text-xs">
                      <span className="font-semibold text-ink">{r.product_name}</span>
                      <span className="font-bold text-ink">{fmtRp(r.margin_contribution)}</span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-stone-100">
                      <div className="h-full rounded-full bg-primary" style={{ width: `${Math.max(6, (r.margin_contribution / max) * 100)}%` }} />
                    </div>
                  </div>
                );
              })}
            </div>
          ) : <Empty label={t("No finished goods to analyze yet")} />}
        </Card>

        {/* Ranking */}
        <RankCard title={t("Margin Ranking")} subtitle={t("Highest-margin products")} icon={Trophy} rows={data.margin_ranking} positive testid="margin-ranking" t={t} />
        {/* Losers */}
        <RankCard title={t("Margin Losers")} subtitle={t("Lowest-margin products")} icon={TrendingDown} rows={data.margin_losers} testid="margin-losers" t={t} />
      </div>

      {/* Main table */}
      <Card className="overflow-hidden p-0" data-testid="margin-table-card">
        <div className="overflow-x-auto"><table className="w-full text-sm">
          <thead><tr className="border-b border-line text-left text-xs uppercase text-ink-mute">
            <th className="px-4 py-3">{t("Product")}</th>
            <th className="px-4 py-3 text-right">{t("Reference HPP")}</th>
            <th className="px-4 py-3 text-right">{t("Transfer Price")}</th>
            <th className="hidden px-4 py-3 text-right md:table-cell">{t("Branch Selling Price")}</th>
            <th className="px-4 py-3 text-right">{t("Central Margin")}</th>
            <th className="hidden px-4 py-3 text-right lg:table-cell">{t("Branch Margin")}</th>
            <th className="hidden px-4 py-3 text-right lg:table-cell">{t("Recommended")}</th>
            <th className="px-4 py-3 text-center">{t("Status")}</th>
            {canManage && <th className="px-4 py-3"></th>}
          </tr></thead>
          <tbody>
            {rows.length === 0 ? (
              <tr><td colSpan={canManage ? 9 : 8} className="px-4 py-10 text-center text-ink-mute" data-testid="margin-empty">
                {t("Set finished-good (transfer/selling) prices to see margins here.")}
              </td></tr>
            ) : rows.map((r) => {
              const st = STATUS[r.status] || STATUS.UNSET;
              return (
                <tr key={r.product_id} className="border-b border-line/60 hover:bg-stone-50/60" data-testid={`margin-row-${r.product_name}`}>
                  <td className="px-4 py-3">
                    <span className="font-semibold text-ink">{r.product_name}</span>
                    <span className="block text-[11px] text-ink-mute">{r.category} · {t(r.product_type === "BRANCH_SKU" ? "Branch Product" : "Central Production Product")}</span>
                  </td>
                  <td className="px-4 py-3 text-right text-ink-soft">{r.hpp_per_unit != null ? fmtRp(r.hpp_per_unit) : "—"}</td>
                  <td className="px-4 py-3 text-right font-semibold">{r.transfer_price > 0 ? fmtRp(r.transfer_price) : "—"}</td>
                  <td className="hidden px-4 py-3 text-right md:table-cell">{r.branch_selling_price > 0 ? fmtRp(r.branch_selling_price) : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    {r.central_margin_pct != null ? (
                      <span className={`font-bold ${r.central_margin_pct < 0 ? "text-danger-text" : "text-ink"}`}>{r.central_margin_pct}%</span>
                    ) : "—"}
                  </td>
                  <td className="hidden px-4 py-3 text-right lg:table-cell">{r.branch_margin_pct != null ? `${r.branch_margin_pct}%` : "—"}</td>
                  <td className="hidden px-4 py-3 text-right text-primary lg:table-cell">{r.recommended_transfer_price != null ? fmtRp(r.recommended_transfer_price) : "—"}</td>
                  <td className="px-4 py-3 text-center"><Badge tone={st.tone} data-testid={`margin-status-${r.product_name}`}>{t(st.label)}</Badge></td>
                  {canManage && <td className="px-4 py-3 text-right">
                    <button onClick={() => setEditRow(r)} className="rounded-lg p-1.5 text-primary hover:bg-primary-light" data-testid={`margin-edit-${r.product_name}`}><Pencil size={15} /></button>
                  </td>}
                </tr>
              );
            })}
          </tbody>
        </table></div>
      </Card>

      {editRow && (
        <PriceModal row={editRow} branchId={branchId}
          branchName={branchId ? (branches.find((b) => b.id === branchId)?.name) : null}
          onClose={() => setEditRow(null)} onSaved={() => { setEditRow(null); load(); }} />
      )}
      {settingsOpen && <SettingsModal onClose={() => setSettingsOpen(false)} onSaved={() => { setSettingsOpen(false); load(); }} />}
    </div>
  );
}

function RankCard({ title, subtitle, icon: Icon, rows, positive, testid, t }) {
  return (
    <Card className="p-4 sm:p-5" data-testid={testid}>
      <div className="mb-3 flex items-center gap-2"><Icon size={16} className="text-primary" />
        <div><h3 className="font-heading text-sm font-extrabold text-ink">{title}</h3>
          <p className="text-[11px] text-ink-mute">{subtitle}</p></div></div>
      {(rows || []).length ? (
        <div className="space-y-1.5">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center justify-between rounded-lg bg-stone-50 px-3 py-2 text-sm" data-testid={`${testid}-${i}`}>
              <span className="flex items-center gap-2 truncate">
                <span className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-white text-[11px] font-bold text-ink-mute">{i + 1}</span>
                <span className="truncate font-semibold text-ink">{r.product_name}</span>
              </span>
              <span className={`shrink-0 font-bold ${(r.central_margin_pct ?? 0) < 0 ? "text-danger-text" : positive ? "text-success-text" : "text-ink"}`}>{r.central_margin_pct}%</span>
            </div>
          ))}
        </div>
      ) : <Empty label={t("No finished goods to analyze yet")} />}
    </Card>
  );
}

function PriceModal({ row, branchId, branchName, onClose, onSaved }) {
  const { t } = useT();
  const toast = useToast();
  const [tp, setTp] = useState(row.transfer_price || "");
  const [sp, setSp] = useState(row.branch_selling_price || "");
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      if (branchId) {
        await api.put("/pricing/branch-prices", {
          branch_id: branchId, product_id: row.product_id,
          transfer_price: tp === "" ? null : Number(tp),
          branch_selling_price: sp === "" ? null : Number(sp),
        });
        toast.success(t("Branch price updated"));
      } else {
        await api.put(`/products/${row.product_id}`, {
          transfer_price: Number(tp) || 0, branch_selling_price: Number(sp) || 0,
        });
        toast.success(t("Branch price updated"));
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    setSaving(false);
  };

  return (
    <Modal open onClose={onClose} size="sm" title={`${t("Set Price")} · ${row.product_name}`}
      footer={<><Button variant="outline" onClick={onClose}>{t("Cancel")}</Button><Button onClick={save} loading={saving} data-testid="margin-save-price">{t("Save Price")}</Button></>}>
      <div className="space-y-3">
        <div className="rounded-xl bg-stone-50 px-3 py-2 text-sm">
          <span className="text-ink-mute">{t("Reference HPP")}: </span>
          <b className="text-ink">{row.hpp_per_unit != null ? fmtRp(row.hpp_per_unit) : "—"}</b>
          {row.recommended_transfer_price != null && (
            <button onClick={() => setTp(row.recommended_transfer_price)} className="ml-3 rounded-lg border border-primary/30 px-2 py-0.5 text-xs font-semibold text-primary hover:bg-primary-light" data-testid="margin-use-recommended">
              {t("Use Recommended")}: {fmtRp(row.recommended_transfer_price)}
            </button>
          )}
        </div>
        <p className="text-xs font-semibold text-ink-soft">{branchId ? `${t("Branch Price Override")} · ${branchName}` : t("Global (all branches)")}</p>
        <div><label className="cf-label">{t("Transfer Price")} (Rp)</label>
          <input type="number" value={tp} onChange={(e) => setTp(e.target.value)} className="cf-input" data-testid="margin-input-transfer" autoFocus /></div>
        <div><label className="cf-label">{t("Branch Selling Price")} (Rp)</label>
          <input type="number" value={sp} onChange={(e) => setSp(e.target.value)} className="cf-input" data-testid="margin-input-selling" /></div>
        {branchId && <p className="text-[11px] text-ink-mute">{t("Leave blank to use the global price.")}</p>}
      </div>
    </Modal>
  );
}

function SettingsModal({ onClose, onSaved }) {
  const { t } = useT();
  const toast = useToast();
  const [f, setF] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/pricing/settings").then(({ data }) => setF(data)); }, []);
  const save = async () => {
    setSaving(true);
    try {
      await api.put("/pricing/settings", {
        margin_low_pct: Number(f.margin_low_pct), margin_healthy_pct: Number(f.margin_healthy_pct),
        target_margin_pct: Number(f.target_margin_pct),
      });
      toast.success(t("Pricing settings updated")); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || t("Failed")); }
    setSaving(false);
  };
  return (
    <Modal open onClose={onClose} size="sm" title={t("Pricing Settings")}
      footer={<><Button variant="outline" onClick={onClose}>{t("Cancel")}</Button><Button onClick={save} loading={saving} disabled={!f} data-testid="margin-save-settings">{t("Save")}</Button></>}>
      {!f ? <PageLoader /> : (
        <div className="space-y-3">
          <p className="cf-label">{t("Margin Thresholds")}</p>
          <NumField label={t("Low Margin Threshold (%)")} value={f.margin_low_pct} onChange={(v) => setF({ ...f, margin_low_pct: v })} testid="set-low" />
          <NumField label={t("Healthy Margin Threshold (%)")} value={f.margin_healthy_pct} onChange={(v) => setF({ ...f, margin_healthy_pct: v })} testid="set-healthy" />
          <NumField label={t("Target Margin for Recommended Price (%)")} value={f.target_margin_pct} onChange={(v) => setF({ ...f, target_margin_pct: v })} testid="set-target" />
        </div>
      )}
    </Modal>
  );
}

function NumField({ label, value, onChange, testid }) {
  return (
    <div className="relative"><label className="cf-label">{label}</label>
      <input type="number" value={value} onChange={(e) => onChange(e.target.value)} className="cf-input pr-8" data-testid={testid} />
      <span className="absolute right-3 top-[34px] text-ink-mute">%</span>
    </div>
  );
}

function BranchChip({ label, icon: Icon, logo, active, onClick, testid }) {
  return (
    <button onClick={onClick} data-testid={testid}
      className={`inline-flex items-center gap-2 rounded-full px-3.5 py-2 text-sm font-semibold transition-all ${active ? "bg-primary text-white shadow-sm" : "bg-white border border-line text-ink-soft hover:bg-stone-50"}`}>
      {logo ? <img src={logo} alt="" className="h-5 w-5 rounded-full object-cover" /> : <Icon size={15} />}
      {label}
    </button>
  );
}

function Kpi({ icon: Icon, label, value, tone = "primary", testid }) {
  const tones = { primary: "bg-primary-light text-primary", info: "bg-blue-50 text-blue-600", warn: "bg-amber-50 text-amber-600", danger: "bg-danger-light text-danger-text" };
  return (
    <Card className="p-4" data-testid={testid}>
      <div className="flex items-center gap-3">
        <span className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tones[tone]}`}><Icon size={18} /></span>
        <div className="min-w-0"><p className="truncate text-xs font-semibold uppercase text-ink-mute">{label}</p><p className="truncate text-lg font-bold text-ink">{value}</p></div>
      </div>
    </Card>
  );
}

const Empty = ({ label }) => <div className="flex h-[160px] items-center justify-center text-sm text-ink-mute">{label}</div>;
